import React, { useState, useEffect, useRef } from "react";

interface Props {
  onClose: () => void;
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export function ContactModal({ onClose }: Props) {
  const [name,    setName]    = useState("");
  const [phone,   setPhone]   = useState("");
  const [email,   setEmail]   = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [ticket,  setTicket]  = useState("");
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`/api/contact`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ name, phone, email, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to send. Please try again.");
      setTicket(data.ticketSerial);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "10px 14px",
    background: "#0d0d0d", border: "1px solid #2a2a2a",
    borderRadius: 8, color: "#e8e8e8", fontSize: 14,
    outline: "none", boxSizing: "border-box",
    fontFamily: "inherit",
  };
  const labelStyle: React.CSSProperties = {
    display: "block", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.14em", textTransform: "uppercase",
    color: "#888", marginBottom: 5,
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#111", border: "1px solid #2a2a2a",
          borderRadius: 16, width: "100%", maxWidth: 500,
          overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
        }}
      >
        {/* Header */}
        <div style={{ background: "#E2622E", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#fff" }}>Contact Us</div>
            <div style={{ fontSize: 12, color: "#fff", opacity: 0.85, marginTop: 2 }}>TubeDealer Support</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", opacity: 0.8, lineHeight: 1 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px 24px 28px" }}>
          {ticket ? (
            /* ── Success state ── */
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#fff", marginBottom: 8 }}>Message Sent!</div>
              <div style={{ fontSize: 13, color: "#aaa", marginBottom: 20, lineHeight: 1.6 }}>
                We've received your message and sent a confirmation to your email.
              </div>
              <div style={{ background: "#181818", border: "1px solid #2a2a2a", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", color: "#E2622E", textTransform: "uppercase", marginBottom: 6 }}>Your Ticket Number</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "monospace" }}>{ticket}</div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>Reference this in any follow-up correspondence</div>
              </div>
              <div style={{ fontSize: 12, color: "#666" }}>We typically respond within 1–2 business days.</div>
              <button
                onClick={onClose}
                style={{ marginTop: 20, padding: "10px 28px", background: "#E2622E", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
              >
                Close
              </button>
            </div>
          ) : (
            /* ── Form ── */
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label htmlFor="contact-name" style={labelStyle}>Name <span style={{ color: "#E2622E" }}>*</span></label>
                  <input
                    id="contact-name"
                    ref={firstRef}
                    value={name} onChange={e => setName(e.target.value)}
                    placeholder="Your full name"
                    style={inputStyle} required
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" style={labelStyle}>Phone</label>
                  <input
                    id="contact-phone"
                    value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="e.g. 555-867-5309"
                    type="tel"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-email" style={labelStyle}>Email <span style={{ color: "#E2622E" }}>*</span></label>
                <input
                  id="contact-email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  type="email"
                  style={inputStyle} required
                />
              </div>

              <div>
                <label htmlFor="contact-message" style={labelStyle}>What are you contacting us about? <span style={{ color: "#E2622E" }}>*</span></label>
                <textarea
                  id="contact-message"
                  value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Describe your question, issue, or feedback..."
                  required
                  rows={5}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 110 }}
                />
              </div>

              {error && (
                <div style={{ background: "#1a0808", border: "1px solid #5a1a1a", borderRadius: 8, padding: "10px 14px", color: "#f87171", fontSize: 13 }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                <button type="button" onClick={onClose}
                  style={{ padding: "10px 20px", background: "none", border: "1px solid #333", borderRadius: 8, color: "#aaa", fontSize: 14, cursor: "pointer" }}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}
                  style={{ padding: "10px 28px", background: loading ? "#7a3518" : "#E2622E", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", transition: "background 0.2s" }}>
                  {loading ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
